import { usePhoneViewport } from "@/hooks/use-phone-viewer-layout";
import {
  Alert,
  cyan,
  DatePicker,
  DatePickerProps,
  Dropdown,
  DropdownProps,
  DropdownSelect,
  FaroButton,
  FaroChipTag,
  FaroText,
  FilterMenuContent,
  FilterMenuOptionType,
  grey,
  NoTranslate,
  Option,
  PlusIcon,
} from "@faro-lotv/flat-ui";
import { GUID, ILabel } from "@faro-lotv/ielement-types";
import {
  Grid,
  LinearProgress,
  Stack,
  SxProps,
  TextField,
  Theme,
} from "@mui/material";
import { DateTime } from "luxon";
import { useEffect, useMemo, useState, type JSX } from "react";
import {
  FileAttachmentsList,
  FileAttachmentsListProps,
} from "./file-attachments-list";
import { SearchableUsersDropdown } from "./searchable-users-dropdown";

/** Generic interface for a tag */
type Tag = Pick<ILabel, "id" | "name">;

// TODO: Check if the style of the dropdown is correct after https://faro01.atlassian.net/browse/SWEB-3745
// Common style applied to all dropdown fields
export const dropDownStyle: SxProps<Theme> = {
  ".MuiSelect-select": {
    // Make the dropdown 38px high, which is the same height as the DatePicker
    py: "7px !important",
  },
  ".MuiTypography-root": {
    fontSize: "0.875rem",
  },
};

/**
 * MUI v6 pulled a `<Grid container spacing={3}>` up by the spacing and padded every item,
 * while v9 puts a `gap` on the container instead. Spreading this on the container, together
 * with `legacyGridItemRowPadding` on the items that did not opt out with `py: 0`, keeps the
 * v6 layout unchanged.
 */
export const legacyGridSpacing = {
  rowSpacing: 0,
  columnSpacing: 3,
  sx: { mt: -3 },
};

/** Top padding a v6 `Grid` item received from `spacing={3}` */
export const legacyGridItemRowPadding: SxProps<Theme> = { pt: 3 };

export type AnnotationEditorMetaFieldsProps = FileAttachmentsListProps & {
  /** Current value of the assignee dropdown field. By default the annotation is unassigned */
  assignee?: string;

  /** Initial value of the due date field. To get the current value use onDueDateChange */
  initialDate?: Date;

  /** Current value of the status dropdown field. By default the status is unassigned */
  status?: string;

  /**
   * List of available option to select in the assignee dropdown field.
   * An option to unselect the assignee will be added automatically to the list
   */
  assigneeOptions: Option[];

  /**
   * List of available option to select in the status dropdown field
   * An option to unselect the status will be added automatically to the list
   */
  statusOptions: Option[];

  /** List of all tags available for this element*/
  tagOptions: FilterMenuOptionType[];

  /** List of the tags selected by the user */
  tags: Tag[];

  /** Callback executed when clicking on the "Manage tags" button*/
  onManageTagsClick(): void;

  /** Function called when the selected value of the assignee dropdown field changes */
  onAssigneeChange(id?: GUID): void;

  /** Function called when the selected due date value changes */
  onDueDateChange?: DatePickerProps["onChange"];

  /** Function called when the selected value of the status dropdown field changes */
  onStatusChange?: DropdownProps["onChange"];

  /** Function called when the user changes the selected tags */
  onTagsChange(tags: Tag[]): void;

  /** If true the attachments list will be shown */
  shouldShowAttachments: boolean;

  /** if `true` the component will be disabled */
  disabled: boolean;

  /** The progress in percentage for the upload of the attachments */
  progress?: number;
};

/**
 * @returns UI to allow to set and adjust the meta fields of an annotation (status, assignee, etc.)
 */
export function AnnotationEditorMetaFields({
  assignee,
  initialDate,
  status,
  assigneeOptions,
  statusOptions,
  tagOptions,
  attachments,
  tags,
  addNewAttachment,
  onAssigneeChange,
  onDueDateChange,
  onStatusChange,
  onTagsChange,
  onAttachmentOpened,
  onManageTagsClick,
  shouldShowAttachments,
  disabled: isSaving,
  progress,
}: AnnotationEditorMetaFieldsProps): JSX.Element {
  const isPhone = usePhoneViewport();
  const [dueDate, setDueDate] = useState(initialDate);

  function changeDueDate(date?: Date): void {
    setDueDate(date);
    onDueDateChange?.(date);
  }
  /** Assign dropdown options that also contain an option to unassign */
  const fullStatusOptions = useMemo(
    () => [
      { key: "unassigned", value: "", label: "Unclassified" },
      ...statusOptions,
    ],
    [statusOptions],
  );

  const [selectedTagOptions, setSelectedTagOptions] = useState(() =>
    generateSelectedTagOptions(tags),
  );

  useEffect(() => {
    setSelectedTagOptions(generateSelectedTagOptions(tags));
  }, [tags]);

  const untranslatedTagOptions = useMemo(
    () =>
      tagOptions.map((tag) => ({
        ...tag,
        label:
          tag.label === "Untagged" ? (
            tag.label
          ) : (
            <NoTranslate>{tag.label}</NoTranslate>
          ),
      })),
    [tagOptions],
  );

  return (
    <>
      <Grid container columnSpacing={3} rowSpacing={isPhone ? 2 : 0}>
        <Grid size={isPhone ? 12 : 6} sx={{ py: 0 }}>
          <SearchableUsersDropdown
            selectedOption={assignee}
            options={assigneeOptions}
            onOptionChange={onAssigneeChange}
            disabled={isSaving}
          />
        </Grid>
        <Grid size={isPhone ? 12 : 6}>
          {isPhone ? (
            <TextField
              label="Due date"
              type="date"
              fullWidth
              disabled={isSaving}
              value={
                dueDate ? (DateTime.fromJSDate(dueDate).toISODate() ?? "") : ""
              }
              onChange={(event) => {
                const date = DateTime.fromISO(event.target.value);
                changeDueDate(date.isValid ? date.toJSDate() : undefined);
              }}
              slotProps={{ inputLabel: { shrink: true } }}
              sx={{
                colorScheme: "dark",
                "& .MuiInputBase-root": {
                  color: grey[100],
                  bgcolor: grey[1000],
                  minHeight: 44,
                },
                "& input": { color: grey[100], bgcolor: "transparent" },
                "& .MuiInputLabel-root": {
                  color: grey[100],
                  "&.Mui-focused": { color: grey[100] },
                  position: "static",
                  transform: "none",
                  mb: 0.5,
                  fontSize: 14,
                },
                "& legend": { display: "none" },
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: grey[500],
                },
              }}
            />
          ) : (
            <DatePicker
              label="Due date"
              disabled={isSaving}
              date={dueDate}
              initialDate={dueDate}
              variant="Dark"
              formSx={{ width: "100%" }}
              onChange={changeDueDate}
              isClearable
            />
          )}
        </Grid>
        <Grid size={isPhone ? 12 : 6} sx={{ py: 0 }}>
          <Dropdown
            label="Status"
            disabled={isSaving}
            value={status}
            options={fullStatusOptions}
            dark
            fullWidth
            sx={dropDownStyle}
            onChange={onStatusChange}
            shouldCapitalize={false}
          />
        </Grid>
        <Grid size={isPhone ? 12 : 6} sx={{ py: 0 }}>
          <DropdownSelect
            dark
            disabled={isSaving}
            label="Tags"
            sx={dropDownStyle}
            renderValue={() => {
              if (!tags.length) {
                return (
                  <FaroText
                    variant="bodyM"
                    sx={{
                      fontStyle: "italic",
                      mr: 1,
                      color: grey[500],
                    }}
                  >
                    Select tags
                  </FaroText>
                );
              }

              return <FaroChipTag size="small" label={`${tags.length}`} dark />;
            }}
          >
            <FilterMenuContent
              dark
              disabled={isSaving}
              selectedOptions={selectedTagOptions}
              options={untranslatedTagOptions}
              onResetFilter={() => setSelectedTagOptions([])}
              onFilterChange={(options) =>
                onTagsChange(
                  options.map((o) => ({
                    id: o.key,
                    name: o.value,
                  })),
                )
              }
              // Make the position of the checkbox independent from the height of the container,
              // so that the checkbox does not move when the text field is active or is showing an error
              optionsSx={{
                alignItems: "unset",
                "& .MuiCheckbox-root": { alignItems: "baseline", pt: 0.5 },
                gap: 0,
              }}
            >
              <FaroButton
                variant="ghost"
                disabled={isSaving}
                dark
                sx={{
                  color: cyan[400],
                  textDecoration: "underline",
                  justifyContent: "flex-start",
                }}
                icon={<PlusIcon />}
                onClick={onManageTagsClick}
              >
                Manage tags
              </FaroButton>
            </FilterMenuContent>
          </DropdownSelect>
        </Grid>
      </Grid>
      {shouldShowAttachments && (
        <Stack sx={{ gap: 1 }}>
          <FaroText variant="labelM" sx={{ color: grey[0] }}>
            File attachments
          </FaroText>
          <FileAttachmentsList
            attachments={attachments}
            addNewAttachment={addNewAttachment}
            onAttachmentOpened={onAttachmentOpened}
          />
        </Stack>
      )}
      {progress !== undefined && (
        <Alert variant="info" dark title={`Uploading attachments ${progress}%`}>
          <LinearProgress
            sx={{
              mt: 1,
              "& .MuiLinearProgress-bar": {
                background: cyan[500],
              },
            }}
            variant="determinate"
            value={progress}
          />
        </Alert>
      )}
    </>
  );
}

/**
 * @param tags List of tags to create the selected tag options from
 * @returns List of selected tag options
 */
function generateSelectedTagOptions(tags: Tag[]): FilterMenuOptionType[] {
  return tags.map((tag) => ({
    key: tag.id,
    value: tag.name,
    label:
      tag.name === "Untagged" ? (
        tag.name
      ) : (
        <NoTranslate>{tag.name}</NoTranslate>
      ),
  }));
}
